import {
  queriesRequirements,
  queriesSharedFileRequestConfigurations
} from "@query";
import { WSActionsMenuItem } from "@wingspanhq/fe-component-library/dist/lib/components/WSActions/WSActions";
import {
  Engagement,
  RequirementDefinition,
  RequirementDefinitionStatus,
  RequirementType
} from "@wingspanhq/wingspan-api-sdk";

import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { useSession } from "../../../v3/hooks/useSession";
import { selectorIsSharedFileNotNoneContext } from "../../Requirements/selectors/selectorIsSharedFileNotNoneContext";
import { useEditRequirementDefinitionModal } from "../components/EditRequirementDefinitionModal/useEditRequirementDefinitionModal";
import { useFlowCreateNewVersion } from "../components/FlowCreateNewVersion";
import { useArchiveNotAllowedModal } from "../modals/useArchiveNotAllowedModal";
import { useArchiveRequirementModal } from "../modals/useArchiveRequirementModal";
import { useCreateNewVersionModal } from "../modals/useCreateNewVersionModal";
import { useDeleteNotAllowedModal } from "../modals/useDeleteNotAllowedModal";
import { useDeleteRequirementModal } from "../modals/useDeleteRequirementModal";
import { useRestoreRequirementModal } from "../modals/useRestoreRequirementModal";

type Props = {
  requirementDefinition: RequirementDefinition;
  engagements: Engagement[];
  onDelete?: (requirementDefinition: RequirementDefinition) => void;
  onView?: (requirementDefinition: RequirementDefinition) => void;
};

export const useRequirementDefinitionDefaultActions = () => {
  const archiveRequirementModal = useArchiveRequirementModal();
  const archiveNotAllowedModal = useArchiveNotAllowedModal();
  const createNewVersionModal = useCreateNewVersionModal();
  const flowCreateNewVersion = useFlowCreateNewVersion();
  const deleteRequirementModal = useDeleteRequirementModal();
  const deleteNotAllowedModal = useDeleteNotAllowedModal();
  const restoreRequirementModal = useRestoreRequirementModal();
  const editRequirementDefinitionModal = useEditRequirementDefinitionModal();

  // v3 filter, so it needs the v3 account id. `useUserId()` reads the v1
  // session's `userId`, which is undefined on a v3 session — it serialised to
  // `filter[payerAccountId][in]=null` and the endpoint rejected the whole
  // request with 422, leaving the menu stuck on "Error loading actions" and
  // every action (Edit / New version / Archive / Restore / Delete) unreachable.
  const { accountId } = useSession();
  const queryFeatureFlags = useQueryFeatureFlags();

  const getOnPreview = (requirementDefinition: RequirementDefinition) => {
    // TODO(v3-blocker): the v3 `DocumentTemplate` model (spec §6 item 4, HelloSign
    // template authoring) has no `viewUrl` equivalent — the HelloSign preview is
    // blocked until BE exposes one. Preview is disabled for Signature until then.
    const onPreviewByType: Partial<
      Record<RequirementType, undefined | (() => Promise<void>)>
    > = {
      [RequirementType.SIGNATURE]: undefined,
      [RequirementType.DOCUMENT_UPLOAD]: undefined,
      [RequirementType.BACKGROUND_CHECK]: undefined,
      [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: undefined,
      [RequirementType.INSURANCE_COVERAGE]: undefined,
      [RequirementType.REGISTRATION]: undefined,
      [RequirementType.TAX_VERIFICATION]: undefined,
      [RequirementType.PAYOUT_METHOD]: undefined
    };

    return onPreviewByType[requirementDefinition.type!];
  };

  const getOnEdit = (requirementDefinition: RequirementDefinition) => {
    const onEdit = () => {
      editRequirementDefinitionModal.open({
        requirementDefinitionId: requirementDefinition.id!
      });
    };

    const sharedFileRequestSettings = requirementDefinition.dataSourceId
      ? queriesSharedFileRequestConfigurations.getSharedFileRequestConfiguration.getQueryData(
          {
            sharedFileRequestConfigurationId: requirementDefinition.dataSourceId
          }
        )
      : undefined;

    const onEditByType: Partial<
      Record<RequirementType, undefined | (() => void)>
    > = {
      [RequirementType.SIGNATURE]: onEdit,
      [RequirementType.DOCUMENT_UPLOAD]:
        sharedFileRequestSettings &&
        !selectorIsSharedFileNotNoneContext(
          sharedFileRequestSettings?.contextType
        )
          ? onEdit
          : undefined,
      [RequirementType.BACKGROUND_CHECK]: onEdit,
      [RequirementType.BIOMETRIC_IDENTITY_VERIFICATION]: onEdit,
      [RequirementType.INSURANCE_COVERAGE]: onEdit,
      [RequirementType.REGISTRATION]: onEdit,
      [RequirementType.TAX_VERIFICATION]: onEdit,
      [RequirementType.PAYOUT_METHOD]: onEdit
    };

    return onEditByType[requirementDefinition.type!];
  };

  const getMenuActions = async (props: Props): Promise<WSActionsMenuItem[]> => {
    const actions: WSActionsMenuItem[] = [];

    // -- Top section: View, Preview & Edit --
    if (props.onView) {
      actions.push({
        icon: "view",
        label: "View requirement",
        onClick: () => props.onView?.(props.requirementDefinition)
      });
    }

    if (getOnPreview(props.requirementDefinition)) {
      actions.push({
        helpText: "Preview the signature document template",
        icon: "view",
        label: "Preview",
        name: "preview",
        onClick: getOnPreview(props.requirementDefinition)
      });
    }

    if (getOnEdit(props.requirementDefinition)) {
      actions.push({
        helpText: "Update display name or description",
        icon: "edit",
        label: "Edit requirement",
        onClick: getOnEdit(props.requirementDefinition)
      });
    }

    // -- Manage section --

    actions.push({ groupLabel: "MANAGE REQUIREMENT" } as WSActionsMenuItem);

    const allConnectedRequirements = accountId
      ? await queriesRequirements.listPayeeRequirements.forceFetchQueryData({
          filter: {
            payerAccountId: { in: [accountId] },
            requirementDefinitionId: { in: [props.requirementDefinition.id!] }
          },
          page: {
            size: 100
          }
        })
      : undefined;

    const allRequirements = allConnectedRequirements?.data || [];
    const engagementLength = props.engagements.filter(engagement =>
      engagement.requirementIds?.includes(props.requirementDefinition.id!)
    ).length;

    // -- New version --
    if (queryFeatureFlags.data?.requirementEditing) {
      actions.push({
        helpText: "Change expiration, approval, or requirement settings",
        icon: "customize",
        label: "Create new version",
        onClick: async () => {
          const shouldContinue = await createNewVersionModal.open({
            requirementDefinition: props.requirementDefinition
          });

          if (shouldContinue) {
            flowCreateNewVersion.open({
              requirementDefinitionId: props.requirementDefinition.id!
            });
          }
        }
      });
    }

    if (
      props.requirementDefinition.status === RequirementDefinitionStatus.ACTIVE
    ) {
      if (engagementLength > 0) {
        actions.push({
          disabled: true,
          helpText: "Archive the requirement when no longer assigned or in use",
          icon: "archive",
          label: "Archive",
          onDisabledClick: async () => {
            archiveNotAllowedModal.open({
              engagementLength,
              requirementDefinition: props.requirementDefinition
            });
          }
        });
      } else {
        actions.push({
          helpText: "Archive the requirement when no longer assigned or in use",
          icon: "archive",
          label: "Archive",
          onClick: async () => {
            archiveRequirementModal.open({
              engagementLength,
              requirementDefinition: props.requirementDefinition
            });
          }
        });
      }
    } else {
      actions.push({
        icon: "redo",
        label: "Restore",
        onClick: async () => {
          restoreRequirementModal.open({
            engagementLength,
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    }

    // -- Destructive section --

    actions.push({ separator: true } as WSActionsMenuItem);

    if (allRequirements.length > 0) {
      actions.push({
        disabled: true,
        helpText: "Permanently delete if never used by any contractor",
        icon: "trash",
        label: "Delete",
        onDisabledClick: async () => {
          deleteNotAllowedModal.open({
            engagementLength,
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    } else {
      actions.push({
        destructive: true,
        helpText: "Permanently delete if never used by any contractor",
        icon: "trash",
        label: "Delete",
        onClick: async () => {
          deleteRequirementModal.open({
            engagementLength,
            onSuccess: props.onDelete || (() => {}),
            requirementDefinition: props.requirementDefinition
          });
        }
      });
    }

    return actions;
  };

  return { getMenuActions, getOnEdit, getOnPreview };
};
